import { TranslateService } from '@ngx-translate/core';
import { getLocaleForLang } from 'core/utils/i18n.util';

export interface LocalizedMessageDetail {
    code?: string;
    Code?: string;
    params?: any;
    Params?: any;
    fallbackText?: string;
    FallbackText?: string;
}

/**
 * Compose a client-localized message from an API code + params.
 * Falls back to the original Ukrainian text when the key is missing.
 */
export function composeLocalizedMessage(
    translate: TranslateService,
    code: string | undefined | null,
    params?: any,
    fallback?: string,
    namespaces: string[] = ['backendErrors'],
): string {
    if (!code) {
        return fallback || '';
    }

    const normalizedParams = normalizeMessageParams(params, translate.currentLang || 'ua', translate);

    for (const ns of namespaces) {
        const key = `${ns}.${code}`;
        const translated = translate.instant(key, normalizedParams);
        if (translated && translated !== key) {
            return translated;
        }
    }

    const bare = translate.instant(code, normalizedParams);
    if (bare && bare !== code) {
        return bare;
    }

    return fallback || code;
}

export function composeLocalizedMessages(
    translate: TranslateService,
    details: LocalizedMessageDetail[] | undefined | null,
    fallbackMessages?: string[],
): string {
    if (details?.length) {
        return details
            .map(d =>
                composeLocalizedMessage(
                    translate,
                    d.code || d.Code,
                    d.params || d.Params,
                    d.fallbackText || d.FallbackText,
                ),
            )
            .filter(Boolean)
            .join('<br>');
    }

    return (fallbackMessages || []).filter(Boolean).join('<br>');
}

export function composeLocalizedMessageFromApiError(translate: TranslateService, message: any): string {
    const errorCode = message?.ErrorCode ?? message?.errorCode ?? [];
    const errorText = message?.ErrorText ?? message?.errorText ?? [];
    const errorParams = message?.ErrorParams ?? message?.errorParams;

    return composeLocalizedMessage(translate, errorCode[0], errorParams, errorText[0]);
}

/** Read structured details from MultiMessageException / similar API payloads (Pascal or camel). */
export function getErrorMessageDetails(error: any): LocalizedMessageDetail[] | undefined {
    const details = error?.MessageDetails ?? error?.messageDetails;
    return Array.isArray(details) && details.length ? details : undefined;
}

/** Read legacy string messages from MultiMessageException / similar API payloads. */
export function getErrorMessages(error: any): string[] | undefined {
    const messages = error?.Messages ?? error?.messages;
    return Array.isArray(messages) && messages.length ? messages : undefined;
}

function normalizeMessageParams(params: any, lang: string, translate: TranslateService): any {
    if (!params || typeof params !== 'object') {
        return params;
    }

    const result: any = { ...params };
    const locale = getLocaleForLang(lang);

    for (const key of Object.keys(result)) {
        const value = result[key];
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                result[key] = date.toLocaleDateString(locale);
            }
        }
    }

    const periodValue = result.periodicityTimePeriod || result.period;
    if (periodValue) {
        const periodKey = `enums.TimePeriod.${periodValue}`;
        const periodTranslated = translate.instant(periodKey);
        result.period =
            periodTranslated && periodTranslated !== periodKey ? periodTranslated : periodValue;
    }

    return result;
}
